import { t } from '@exam-prep/i18n';
import { cn } from '@exam-prep/ui';
import type { AttemptView, PaletteState } from '../../../lib/api-types';
import { paletteState, type Local, type OpenQuestion } from '../model/palette';

/** Exam-standard palette colours; each pairs with white text at ≥ 4.5:1. */
const COLOURS: Record<PaletteState, string> = {
  not_visited: 'bg-card',
  not_answered: 'border-transparent bg-[#b91c1c] text-white',
  answered: 'border-transparent bg-[#15803d] text-white',
  marked: 'border-transparent bg-[#6d28d9] text-white',
  answered_marked: 'border-transparent bg-[#6d28d9] text-white ring-2 ring-[#4ade80] ring-inset',
};

export function PalettePanel({
  sections,
  local,
  questions,
  currentId,
  disabled,
  onVisit,
}: {
  sections: AttemptView['sections'];
  local: Readonly<Record<string, Local>>;
  questions: readonly OpenQuestion[];
  currentId: string | undefined;
  disabled: boolean;
  onVisit: (at: number) => void;
}) {
  return (
    <section className="rounded-2xl border bg-card p-3 backdrop-blur-md">
      <p className="mb-2 font-display text-sm font-semibold">{t('miniapp.player.palette')}</p>
      {sections
        .filter((s) => s.state === 'open')
        .map((s) => (
          <div key={s.key} className="mb-2">
            <p className="mb-1 text-xs text-muted-foreground">{s.subjectName}</p>
            <div className="grid grid-cols-7 gap-1.5">
              {s.palette.map((p) => (
                <button
                  key={p.itemId}
                  type="button"
                  onClick={() => onVisit(questions.findIndex((x) => x.itemId === p.itemId))}
                  disabled={disabled}
                  className={cn(
                    'h-10 rounded-lg border text-xs font-semibold tabular-nums',
                    COLOURS[paletteState(local, p)],
                    currentId === p.itemId &&
                      'outline-2 outline-offset-2 outline-[var(--brand-amber-500)]',
                  )}
                >
                  {p.n}
                </button>
              ))}
            </div>
          </div>
        ))}
      <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-[11px] text-muted-foreground">
        {(Object.keys(COLOURS) as PaletteState[]).map((k) => (
          <span key={k} className="flex items-center gap-1">
            <span className={cn('inline-block size-3.5 rounded border', COLOURS[k])} />
            {t(`miniapp.legend.${k}`)}
          </span>
        ))}
      </div>
    </section>
  );
}
